import "./AddOrganisationCard.css";

import { ErrorBox } from "../../cards";
import { Autocomplete, Button, MultiSelect, Select } from "../../forms";
import { Column, Row } from "../../layout";
import { getErrorMessages, hasFieldError } from "../utils";
import { AddOrganisationCardProps } from "./models";

export const AddOrganisationCard = ({
  organisationTypeOptions,
  organisationOptions,
  accessOptions,
  projectOptions,
  value,
  onChange,
  onOrganisationSearch,
  onAdd,
  corners = "default",
  errors,
  existingDeveloperPrompts = [],
  existingDeveloperActionOptions = [],
}: AddOrganisationCardProps): JSX.Element => {
  const isTypeChosen = !!value.organisationType;

  return (
    <div className="AddOrganisationCard">
      {errors && errors.length > 0 && (
        <Row spacingV="ml">
          <Column span={12}>
            <ErrorBox
              title="Please solve the below issues before adding the organisation"
              text={getErrorMessages(errors)}
            />
          </Column>
        </Row>
      )}

      <div className="AddOrganisationCard_row">
        <div className="AddOrganisationCard_fields">
          <div className="AddOrganisationCard_field">
            <Select
              label="Organisation type"
              placeholder="Select"
              corners={corners}
              data={organisationTypeOptions}
              value={value.organisationType}
              onChange={(organisationType) =>
                onChange({
                  ...value,
                  organisationType,
                  organisationUuid: null,
                  access: null,
                  existingDeveloperActions: {},
                })
              }
              hasError={hasFieldError(errors, "organisationType")}
            />
          </div>
          <div className="AddOrganisationCard_field">
            <Autocomplete
              label="Organisation"
              placeholder={isTypeChosen ? "Search" : "Choose a type first"}
              corners={corners}
              data={organisationOptions}
              initialKey={value.organisationUuid}
              disabled={!isTypeChosen}
              onSelect={(organisationUuid) => onChange({ ...value, organisationUuid })}
              onTypeSearch={onOrganisationSearch}
            />
          </div>
          <div className="AddOrganisationCard_field">
            <Select
              label="Access"
              placeholder={isTypeChosen ? "Select" : "Choose a type first"}
              corners={corners}
              data={accessOptions}
              value={value.access}
              disabled={!isTypeChosen}
              onChange={(access) => onChange({ ...value, access, existingDeveloperActions: {} })}
              hasError={hasFieldError(errors, "access")}
            />
          </div>
          <div className="AddOrganisationCard_field">
            <MultiSelect
              label="Project"
              placeholder={isTypeChosen ? "Select" : "Choose a type first"}
              corners={corners}
              data={projectOptions}
              values={value.projectKeys}
              disabled={!isTypeChosen}
              showSearch
              onChange={(projectKeys) => onChange({ ...value, projectKeys })}
              hasError={hasFieldError(errors, "projectKeys")}
            />
          </div>
        </div>
        <Button text="Add" corners={corners} disabled={!isTypeChosen} onClick={onAdd} />
      </div>

      {existingDeveloperPrompts.length > 0 && (
        <div className="AddOrganisationCard_row AddOrganisationCard_existingDeveloperRow">
          <div className="AddOrganisationCard_fields">
            {existingDeveloperPrompts.map((prompt) => (
              <div className="AddOrganisationCard_field" key={prompt.projectKey}>
                <Select
                  label={`What happens to ${prompt.developerName} on ${prompt.projectName}?`}
                  placeholder="Select"
                  corners={corners}
                  data={existingDeveloperActionOptions}
                  value={value.existingDeveloperActions?.[prompt.projectKey] ?? null}
                  onChange={(action) =>
                    onChange({
                      ...value,
                      existingDeveloperActions: { ...value.existingDeveloperActions, [prompt.projectKey]: action },
                    })
                  }
                  hasError={hasFieldError(errors, "existingDeveloperActions")}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
